import { useState } from "react";
import { useStoreState } from "ezStore";
import { saveAs } from "file-saver";
import { intl } from "LocaleWrapper";

import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { Card, FormGroup, FormGroupProps } from "@cofano/cck";

import { Box, Grid2 as Grid, Tab, Tabs } from "@mui/material";
import EditRounded from "@mui/icons-material/EditRounded";

import { hasPluginPermission, usePermission } from "components/utils/Permission";

import { DataFormat, Language } from "utils";
import { LocationLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import VoyageDetailsCallContainers from "view/voyages/components/details/VoyageDetailsCallContainers";
import ContainerAmountSummary from "view/containers/components/summary/ContainerAmountSummary";
import ExpectedCargosTable from "view/cargos/components/table/ExpectedCargosTable";
import ExpectedCargosCreateEditDialog from "view/cargos/components/dialogs/ExpectedCargosCreateEditDialog";

import { CallType, StatusType } from "__generated__/enums";
import { VoyageDetailsCallContainersBlockCallFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";

export type CallContainersBlockProps = {
	call: VoyageDetailsCallContainersBlockCallFragment;
	hideExpectedCargos: boolean;
	refetch: () => void;
}

const VoyageDetailsCallContainersBlock = ({ call, hideExpectedCargos, refetch }: CallContainersBlockProps) => {

	const session = useStoreState(store => store.session);
	const { instance } = useCoastguard();

	const [ selectedTab, selectTab ] = useState(CallType.Loading);
	const [ expectedCargosEditDialogOpen, setExpectedCargosEditDialogOpen ] = useState(false);

	const { isAdmin, isCompanyUser, isMainUserLimited, isSkipper, isReadOnly } = usePermission();

	const showEditExpectedCargos = !hideExpectedCargos
		&& !isReadOnly && (isAdmin || isCompanyUser || isMainUserLimited || isSkipper)
		&& hasPluginPermission(session, "externalVoyageCallsManagement");

	const downloadLoadReport = () => {
		instance.fetchBlob(`/export/loadReport/${call.id}`, {
			method: "GET",
			headers: {
				Accept: "application/vnd.ms-excel",
			},
		}).then(async (blob) => {
			saveAs(blob, "LoadReport.xlsx");
		});
	};

	const downloadDischargeReport = () => {
		instance.fetchBlob(`/export/dischargeReport/${call.id}`, {
			method: "GET",
			headers: {
				Accept: "application/vnd.ms-excel",
			},
		}).then(async (blob) => {
			saveAs(blob, "DischargeReport.xlsx");
		});
	};

	const formGroupProps: Omit<FormGroupProps, "label"> = {
		labelPlacement: "left-center",
		overflow: "hidden",
	};

	return (
		<Grid spacing={3} container>
			<Grid size={{ xs: 12 }}>
				<Card
					title={intl.formatMessage({ id: "DETAILS" })}
					actions={[ {
						text: intl.formatMessage({ id: "LOAD_REPORT" }),
						onClick: () => downloadLoadReport(),
						hidden: !hasPluginPermission(session, "loadReport"),
					}, {
						text: intl.formatMessage({ id: "DISCHARGE_REPORT" }),
						onClick: () => downloadDischargeReport(),
						hidden: !hasPluginPermission(session, "loadReport"),
					} ]}
				>
					<Grid spacing={3} container>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormGroup {...formGroupProps} label={Language("LOCATION")}>
								<LocationLink location={call.location} />
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("STATUS")}>
								<StatusChanger
									session={session}
									entity={call}
									statusType={StatusType.Call}
									refetch={refetch}
									showHistory
								/>
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("LOADING_WEIGHT")}>
								{DataFormat.tonnage(call.loadedWeight)}
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("DISCHARGING_WEIGHT")}>
								{DataFormat.tonnage(call.dischargedWeight)}
							</FormGroup>

						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<div style={{ marginBottom: 16 }}>
								<FormGroup {...formGroupProps} label={Language("PLANNING")}>
									<FormattedDateWindow from={call.pta} to={call.ptd} />
								</FormGroup>
							</div>

							<FormGroup {...formGroupProps} label={""}>
								<Grid spacing={3} container>
									<Grid size={{ xs: 6 }}>
										<strong>{Language("EXPECTED")}</strong>
									</Grid>

									<Grid size={{ xs: 6 }}>
										<strong>{Language("ACTUAL")}</strong>
									</Grid>
								</Grid>
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("ARRIVAL")}>
								<Grid spacing={3} container>
									<Grid size={{ xs: 6 }}>
										{call.eta ? <FormattedDate date={call.eta} /> : Language("UNKNOWN")}
									</Grid>

									<Grid size={{ xs: 6 }}>
										{call.ata ? <FormattedDate date={call.ata} /> : Language("UNKNOWN")}
									</Grid>
								</Grid>
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("START_COMPLETION")}>
								<Grid spacing={3} container>
									<Grid size={{ xs: 6 }}>
										{call.etso ? <FormattedDate date={call.etso} /> : Language("UNKNOWN")}
									</Grid>

									<Grid size={{ xs: 6 }}>
										{call.atso ? <FormattedDate date={call.atso} /> : Language("UNKNOWN")}
									</Grid>
								</Grid>
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("END_COMPLETION")}>
								<Grid spacing={3} container>
									<Grid size={{ xs: 6 }}>
										{call.eteo ? <FormattedDate date={call.eteo} /> : Language("UNKNOWN")}
									</Grid>

									<Grid size={{ xs: 6 }}>
										{call.ateo ? <FormattedDate date={call.ateo} /> : Language("UNKNOWN")}
									</Grid>
								</Grid>
							</FormGroup>

							<FormGroup {...formGroupProps} label={Language("DEPARTURE")}>
								<Grid spacing={3} container>
									<Grid size={{ xs: 6 }}>
										{call.etd ? <FormattedDate date={call.etd} /> : Language("UNKNOWN")}
									</Grid>

									<Grid size={{ xs: 6 }}>
										{call.atd ? <FormattedDate date={call.atd} /> : Language("UNKNOWN")}
									</Grid>
								</Grid>
							</FormGroup>
						</Grid>
					</Grid>
				</Card>
			</Grid>

			{!hideExpectedCargos && (
				<Grid size={{ xs: 12 }}>
					<Card
						title={Language("EXPECTED_CONTAINERS")}
						actions={[ {
							onClick: () => setExpectedCargosEditDialogOpen(true),
							icon: <EditRounded />,
							hidden: !showEditExpectedCargos,
						} ]}
						disablePadding
					>
						<ContainerAmountSummary
							loadingAmount={call.expectedLoadingAmount ?? null}
							dischargingAmount={call.expectedDischargingAmount ?? null}
							loadingTeu={call.expectedLoadingTeu ?? null}
							dischargingTeu={call.expectedDischargingTeu ?? null}
						/>
						<ExpectedCargosTable expectedCargos={call.expectedCargos} />
					</Card>
					{showEditExpectedCargos && (
						<ExpectedCargosCreateEditDialog
							open={expectedCargosEditDialogOpen}
							callExpectedCargos={call.expectedCargos.map(expectedCargo => ({
								...expectedCargo,
								dischargingLocation: expectedCargo.dischargingLocation ?? null,
							}))}
							onClose={() => setExpectedCargosEditDialogOpen(false)}
							callId={call.id}
							refetch={refetch}
						/>
					)}
				</Grid>
			)}

			<Grid size={{ xs: 12 }}>
				<Card title={Language("CONTAINERS")} disablePadding>
					<ContainerAmountSummary
						loadingAmount={call.nrLoadingCargos ?? null}
						dischargingAmount={call.nrDischargingCargos ?? null}
						loadingTeu={call.callLoadingTeu ?? null}
						dischargingTeu={call.callDischargingTeu ?? null}
					/>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={selectedTab}
							indicatorColor="primary"
							onChange={(_, value) => selectTab(value)}
							variant="scrollable">
							<Tab label={Language("LOADING")} value={CallType.Loading} />
							<Tab label={Language("DISCHARGING")} value={CallType.Discharging} />
						</Tabs>
					</Box>

					<VoyageDetailsCallContainers
						call={call}
						type={selectedTab}
						key={`${call.id}-${selectedTab}`}
					/>
				</Card>
			</Grid>
		</Grid>
	);
};

export default VoyageDetailsCallContainersBlock;
